import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import TablePagination from "../../components/shared/TablePagination";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  useCreatePaymentMutation,
  useDeletePaymentMutation,
  useFetchPaymentsQuery,
  useUpdatePaymentMutation,
  useUpdatePaymentStatusMutation,
  useUploadPaymentInvoiceMutation,
  type CreatePaymentPayload,
  type PaymentMethod,
  type PaymentRecord,
  type PaymentStatus,
} from "../../store/api/paymentsApi";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { exportToExcel } from "../../utils/exportExcel";
import "./admin.css";

// ── Constants ────────────────────────────────────────────────────────────────

const PAYMENT_METHODS: PaymentMethod[] = ["Cash", "BankTransfer", "Cheque", "UPI", "NEFT", "RTGS"];
const STATUSES: PaymentStatus[] = ["Pending", "Received", "Verified"];

const EMPTY_FORM: CreatePaymentPayload = {
  customerName: "",
  projectName: "",
  amount: 0,
  paymentDate: new Date().toISOString().slice(0, 10),
  paymentMethod: "Cash",
  referenceNumber: "",
  notes: "",
  status: "Pending",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const statusBadge = (status: PaymentStatus) => {
  if (status === "Verified") return "text-bg-success";
  if (status === "Received") return "text-bg-primary";
  return "text-bg-warning";
};

const formatAmount = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);

// ── Inline Invoice-upload row ────────────────────────────────────────────────

const InvoiceUploadRow: React.FC<{ payment: PaymentRecord; onError: (msg: string) => void }> = ({
  payment,
  onError,
}) => {
  const [uploadInvoice, { isLoading }] = useUploadPaymentInvoiceMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadInvoice({ id: payment.id, file }).unwrap();
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      onError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {payment.invoiceUrl ? (
        <a
          href={payment.invoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline-secondary btn-sm"
          title="Download invoice"
        >
          <i className="bi bi-file-earmark-arrow-down me-1" />
          {payment.invoiceFileName ? payment.invoiceFileName.slice(0, 20) : "Invoice"}
        </a>
      ) : (
        <span className="small text-muted">No invoice</span>
      )}
      <label
        className="btn btn-outline-primary btn-sm mb-0"
        title={payment.invoiceUrl ? "Replace invoice" : "Upload invoice"}
        style={{ cursor: isLoading ? "wait" : "pointer" }}
      >
        {isLoading ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          <i className="bi bi-cloud-upload" />
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="d-none"
          onChange={handleChange}
          disabled={isLoading}
          aria-label="Upload invoice file"
          title="Upload invoice file"
        />
      </label>
    </div>
  );
};

// ── Add / Edit Form ──────────────────────────────────────────────────────────

interface PaymentFormProps {
  initial?: PaymentRecord | null;
  onClose: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ initial, onClose }) => {
  const [form, setForm] = useState<CreatePaymentPayload>(
    initial
      ? {
          customerName: initial.customerName,
          projectName: initial.projectName ?? "",
          amount: initial.amount,
          paymentDate: initial.paymentDate,
          paymentMethod: initial.paymentMethod,
          referenceNumber: initial.referenceNumber ?? "",
          notes: initial.notes ?? "",
          status: initial.status,
        }
      : EMPTY_FORM
  );
  const [error, setError] = useState("");
  const [createPayment, { isLoading: creating }] = useCreatePaymentMutation();
  const [updatePayment, { isLoading: updating }] = useUpdatePaymentMutation();

  const busy = creating || updating;

  const set = (key: keyof CreatePaymentPayload, value: string | number) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.customerName.trim()) { setError("Customer name is required."); return; }
    if (!form.amount || form.amount <= 0) { setError("Enter a valid amount greater than zero."); return; }
    if (!form.paymentDate) { setError("Payment date is required."); return; }

    try {
      if (initial) {
        await updatePayment({ id: initial.id, ...form }).unwrap();
      } else {
        await createPayment(form).unwrap();
      }
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const isEditMode = Boolean(initial);
  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
      <div className="row g-3 align-items-end">
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
              Customer Name <span className="text-danger">*</span>
              <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Full name of the paying customer</small>
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#6366f1" }}><i className="bi bi-person-fill" style={{ fontSize: 12 }} /></span>
              <input className="form-control form-control-sm" style={{ borderColor: "#c7d2fe", boxShadow: "none" }} title="Customer name" value={form.customerName} onChange={(e) => set("customerName", e.target.value)} disabled={busy} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
              Project Name
              <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Link to a specific project (optional)</small>
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#e0f2fe", borderColor: "#bae6fd", color: "#0ea5e9" }}><i className="bi bi-building" style={{ fontSize: 12 }} /></span>
              <input className="form-control form-control-sm" style={{ borderColor: "#bae6fd", boxShadow: "none" }} title="Project name (optional)" value={form.projectName ?? ""} onChange={(e) => set("projectName", e.target.value)} disabled={busy} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
              Amount (₹) <span className="text-danger">*</span>
              <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Total rupee amount received</small>
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#d1fae5", borderColor: "#a7f3d0", color: "#059669", fontWeight: 700 }}>₹</span>
              <input type="number" min={1} step="0.01" className="form-control form-control-sm" style={{ borderColor: "#a7f3d0", boxShadow: "none" }} title="Payment amount in rupees" value={form.amount || ""} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)} disabled={busy} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
              Payment Date <span className="text-danger">*</span>
              <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Date the payment was received</small>
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#fef3c7", borderColor: "#fcd34d", color: "#d97706" }}><i className="bi bi-calendar-date-fill" style={{ fontSize: 12 }} /></span>
              <input type="date" className="form-control form-control-sm" style={{ borderColor: "#fcd34d", boxShadow: "none" }} title="Payment date" value={form.paymentDate} onChange={(e) => set("paymentDate", e.target.value)} disabled={busy} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>Payment Method</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#ede9fe", borderColor: "#ddd6fe", color: "#7c3aed" }}><i className="bi bi-credit-card-fill" style={{ fontSize: 12 }} /></span>
              <select className="form-select form-select-sm" style={{ borderColor: "#ddd6fe", boxShadow: "none" }} value={form.paymentMethod} onChange={(e) => set("paymentMethod", e.target.value as PaymentMethod)} disabled={busy} title="Select payment method" aria-label="Payment method">
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #14b8a6", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
              Reference / Cheque No.
              <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Bank ref, cheque no., or UPI ID</small>
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#ccfbf1", borderColor: "#99f6e4", color: "#0f766e" }}><i className="bi bi-hash" style={{ fontSize: 12 }} /></span>
              <input className="form-control form-control-sm" style={{ borderColor: "#99f6e4", boxShadow: "none" }} title="Reference or cheque number" value={form.referenceNumber ?? ""} onChange={(e) => set("referenceNumber", e.target.value)} disabled={busy} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>Status</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={{ background: "#ffe4e6", borderColor: "#fecdd3", color: "#e11d48" }}><i className="bi bi-check-circle-fill" style={{ fontSize: 12 }} /></span>
              <select className="form-select form-select-sm" style={{ borderColor: "#fecdd3", boxShadow: "none" }} value={form.status} onChange={(e) => set("status", e.target.value as PaymentStatus)} disabled={busy} title="Payment status" aria-label="Payment status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div style={{ borderLeft: "3px solid #94a3b8", paddingLeft: 10 }}>
            <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
              Notes
              <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Internal remarks about this payment (optional)</small>
            </label>
            <textarea className="form-control form-control-sm" style={{ borderColor: "#cbd5e1", boxShadow: "none" }} rows={2} title="Internal notes" value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} disabled={busy} />
          </div>
        </div>
      </div>
      <div style={{ borderTop: "1.5px dashed #e2e8f0", paddingTop: 14, display: "flex", gap: 8, marginTop: 16 }}>
        <button type="submit" disabled={busy} style={{ background: isEditMode ? "linear-gradient(135deg, #d97706, #f59e0b)" : "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 20px", fontSize: "0.83rem", fontWeight: 700, cursor: busy ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: isEditMode ? "0 3px 10px #d9770633" : "0 3px 10px #0d948833" }}>
          {busy ? (
            <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> {initial ? "Updating…" : "Saving…"}</>
          ) : (
            <><i className={isEditMode ? "bi bi-check2-square" : "bi bi-plus-circle-fill"} />{isEditMode ? "Update Payment" : "Record Payment"}</>
          )}
        </button>
        <button type="button" style={{ background: "#f1f5f9", border: "1.5px solid #cbd5e1", color: "#64748b", borderRadius: 8, padding: "6px 16px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }} onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────

const PaymentManager: React.FC = () => {
  const { data: payments = [], isLoading, error: fetchError } = useFetchPaymentsQuery();
  const [deletePayment, { isLoading: isDeleting }] = useDeletePaymentMutation();
  const [updateStatus] = useUpdatePaymentStatusMutation();

  const [editingPayment, setEditingPayment] = useState<PaymentRecord | null>(null);
  const [actionError, setActionError] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const fetchErrorMessage = fetchError ? getApiErrorMessage(fetchError as FetchBaseQueryError) : "";

  const filtered = payments.filter((p) => {
    const matchesStatus = statusFilter === "All" || p.status === statusFilter;
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      p.customerName.toLowerCase().includes(q) ||
      (p.projectName ?? "").toLowerCase().includes(q) ||
      (p.referenceNumber ?? "").toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedPayments = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const totalAmount = filtered.reduce((s, p) => s + p.amount, 0);
  const verifiedCount = payments.filter((p) => p.status === "Verified").length;
  const pendingCount = payments.filter((p) => p.status === "Pending").length;

  const handleDelete = async (id: number) => {
    setActionError("");
    try {
      await deletePayment(id).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const handleStatusChange = async (id: number, status: PaymentStatus) => {
    setActionError("");
    try {
      await updateStatus({ id, status }).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const openEdit = (p: PaymentRecord) => setEditingPayment(p);
  const closeForm = () => setEditingPayment(null);

  const headerGradient = editingPayment
    ? "linear-gradient(135deg, #78350f, #d97706, #f59e0b)"
    : "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)";

  return (
    <section className="admin-page">
      {/* ── KPI Strip ── */}
      <div className="dash-kpi-grid dash-kpi-grid--5col mb-4">
        <article className="dash-stat-card dash-stat-card--blue dash-stat-card--flat">
          <div className="dash-stat-flat-top">
            <div className="dash-stat-icon"><i className="bi bi-cash-stack" /></div>
            <h3 className="dash-stat-value">{isLoading ? "…" : payments.length}</h3>
          </div>
          <div className="dash-stat-body">
            <p className="dash-stat-label">Total Payments</p>
            <span className="dash-stat-sub">all time</span>
          </div>
        </article>
        <article className="dash-stat-card dash-stat-card--green dash-stat-card--flat">
          <div className="dash-stat-flat-top">
            <div className="dash-stat-icon"><i className="bi bi-patch-check-fill" /></div>
            <h3 className="dash-stat-value">{isLoading ? "…" : verifiedCount}</h3>
          </div>
          <div className="dash-stat-body">
            <p className="dash-stat-label">Verified</p>
            <span className="dash-stat-sub">confirmed receipts</span>
          </div>
        </article>
        <article className="dash-stat-card dash-stat-card--gold dash-stat-card--flat">
          <div className="dash-stat-flat-top">
            <div className="dash-stat-icon"><i className="bi bi-hourglass-split" /></div>
            <h3 className="dash-stat-value">{isLoading ? "…" : pendingCount}</h3>
          </div>
          <div className="dash-stat-body">
            <p className="dash-stat-label">Pending</p>
            <span className="dash-stat-sub">awaiting confirmation</span>
          </div>
        </article>
        <article className="dash-stat-card dash-stat-card--violet dash-stat-card--flat" style={{ gridColumn: "span 2" }}>
          <div className="dash-stat-flat-top">
            <div className="dash-stat-icon"><i className="bi bi-currency-rupee" /></div>
            <h3 className="dash-stat-value">{isLoading ? "…" : formatAmount(totalAmount)}</h3>
          </div>
          <div className="dash-stat-body">
            <p className="dash-stat-label">Total Received (filtered)</p>
            <span className="dash-stat-sub">sum of listed payments</span>
          </div>
        </article>
      </div>

      {/* ── Single card ── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
        {/* Gradient header */}
        <div style={{ background: headerGradient, padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className={editingPayment ? "bi bi-pencil-square" : "bi bi-cash-coin"} style={{ color: "#fff", fontSize: 14 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>
                {editingPayment ? `Edit Payment — ${editingPayment.customerName}` : "Record New Payment"}
              </div>
              <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>
                {editingPayment ? "Update the fields below, then save" : "Fill in customer payment details below"}
              </div>
            </div>
          </div>
          {editingPayment && (
            <span style={{ background: "rgba(255,255,255,0.22)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>Editing</span>
          )}
        </div>

        {/* Form — always visible */}
        <div className="p-4 border-bottom">
          <PaymentForm initial={editingPayment} onClose={closeForm} />
        </div>

      </div>{/* ── end form card ── */}

      {/* ── Payment Records card (matches Expense page style) ── */}
      <div className="card border-0 shadow-sm rounded-4">

        {/* Filter bar */}
        <div className="d-flex flex-wrap gap-3 align-items-center p-3 border-bottom">
          <div style={{ minWidth: 220 }}>
            <input
              className="form-control form-control-sm"
              placeholder="Search customer, project, or reference…"
              title="Search by customer name, project, or reference"
              aria-label="Search customer, project, or reference"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 160 }}>
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="Filter by status"
              aria-label="Filter by status"
            >
              <option value="All">All Statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {(searchQuery || statusFilter !== "All") && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              title="Clear filters"
              onClick={() => { setSearchQuery(""); setStatusFilter("All"); }}
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-2"
            onClick={() =>
              exportToExcel(
                filtered.map((p, i) => ({
                  "#": i + 1,
                  Customer: p.customerName,
                  Project: p.projectName ?? "—",
                  "Amount (₹)": p.amount,
                  Date: p.paymentDate,
                  Method: p.paymentMethod,
                  Reference: p.referenceNumber ?? "—",
                  Status: p.status,
                })),
                "Payments"
              )
            }
          >
            <i className="bi bi-file-earmark-excel" /> Export
          </button>
          <span className="small text-muted ms-auto">
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            <span className="ms-2 text-success fw-semibold">· {formatAmount(totalAmount)}</span>
          </span>
        </div>

        <div className="card-body p-4">

          {/* Errors */}
          {(fetchErrorMessage || actionError) && (
            <div className="alert alert-danger mb-3">{fetchErrorMessage || actionError}</div>
          )}

          {isLoading ? (
            <div className="dash-empty-state">Loading payments…</div>
          ) : filtered.length === 0 ? (
            <div className="dash-empty-state">
              <i className="bi bi-cash-stack fs-2 d-block mb-2" />
              {searchQuery || statusFilter !== "All"
                ? "No records match your filter."
                : "No payments recorded yet. Use the form above to add the first one."}
            </div>
          ) : (
            <>
              {/* ── Desktop table ── */}
              <div className="table-responsive d-none d-lg-block">
                <table className="table align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Customer</th>
                      <th>Project</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Invoice</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedPayments.map((payment, idx) => (
                      <tr
                        key={payment.id}
                        className={editingPayment?.id === payment.id ? "table-warning" : ""}
                      >
                        <td className="text-muted small">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                        <td>
                          <div className="fw-semibold">{payment.customerName}</div>
                          {payment.notes && (
                            <div className="small text-muted text-truncate" style={{ maxWidth: 180 }} title={payment.notes}>
                              {payment.notes}
                            </div>
                          )}
                        </td>
                        <td className="text-muted small">{payment.projectName || "—"}</td>
                        <td>
                          <span className="fw-bold text-success">{formatAmount(payment.amount)}</span>
                        </td>
                        <td className="text-muted small">{payment.paymentDate}</td>
                        <td>
                          <span className="badge text-bg-secondary rounded-pill px-2 py-1">
                            {payment.paymentMethod}
                          </span>
                        </td>
                        <td className="text-muted small">{payment.referenceNumber || "—"}</td>
                        <td>
                          <InvoiceUploadRow payment={payment} onError={setActionError} />
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            <select
                              className="form-select form-select-sm"
                              style={{ minWidth: 110 }}
                              value={payment.status}
                              onChange={(e) => handleStatusChange(payment.id, e.target.value as PaymentStatus)}
                              title="Change payment status"
                              aria-label="Change payment status"
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <span className={`badge rounded-pill ${statusBadge(payment.status)}`}>
                              {payment.status}
                            </span>
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm admin-icon-btn"
                              onClick={() => openEdit(payment)}
                              title="Edit payment"
                              aria-label={`Edit ${payment.customerName}`}
                            >
                              <i className="bi bi-pencil-fill" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm admin-icon-btn"
                              onClick={() => handleDelete(payment.id)}
                              disabled={isDeleting}
                              title="Delete payment"
                              aria-label={`Delete ${payment.customerName}`}
                            >
                              <i className="bi bi-trash-fill" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="exp-tfoot-total">
                      <td colSpan={3} className="text-end fw-bold text-muted">Total Shown</td>
                      <td className="fw-bold text-dark">{formatAmount(totalAmount)}</td>
                      <td colSpan={6} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* ── Mobile cards ── */}
              <div className="d-lg-none exp-mobile-list">
                {pagedPayments.map((payment, idx) => (
                  <div
                    key={payment.id}
                    className={`exp-mobile-card${editingPayment?.id === payment.id ? " border-warning" : ""}`}
                  >
                    <div className="exp-mobile-top">
                      <span className="exp-mobile-index">{(currentPage - 1) * PAGE_SIZE + idx + 1}</span>
                      <span className="fw-semibold flex-grow-1">{payment.customerName}</span>
                      <span className="fw-bold text-success">{formatAmount(payment.amount)}</span>
                    </div>
                    <div className="exp-mobile-meta">
                      <span className="text-muted small">{payment.projectName || "—"}</span>
                      <span className="badge text-bg-secondary rounded-pill">{payment.paymentMethod}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                      <span className="small text-muted">{payment.paymentDate}</span>
                      <span className={`badge rounded-pill ${statusBadge(payment.status)}`}>{payment.status}</span>
                      <InvoiceUploadRow payment={payment} onError={setActionError} />
                    </div>
                    {payment.notes && (
                      <div className="small text-muted mt-1 text-truncate" title={payment.notes}>{payment.notes}</div>
                    )}
                    <div className="admin-table-actions mt-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm admin-icon-btn"
                        onClick={() => openEdit(payment)}
                        title="Edit"
                        aria-label={`Edit ${payment.customerName}`}
                      >
                        <i className="bi bi-pencil-fill" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm admin-icon-btn"
                        onClick={() => handleDelete(payment.id)}
                        disabled={isDeleting}
                        title="Delete"
                        aria-label={`Delete ${payment.customerName}`}
                      >
                        <i className="bi bi-trash-fill" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </section>
  );
};

export default PaymentManager;
